import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import useAuth from "../../../hooks/useAuth";
import { colors } from "../../../theme/tokens";
import { menuItems } from "./menuConfig";

// Texto/ícones sobre o fundo escuro da sidebar (Seção 02 da ux-design-spec:
// "Azul petróleo profundo" como cor de chrome, não só de acento) reaproveitam
// os tokens de texto do modo dark já existentes em tokens.js — o app inteiro
// continua em light mode, só a sidebar é uma "ilha" de superfície escura
// quando `chromeStyle === "blue"` (padrão). `chromeStyle === "white"` é a
// opção de fallback pro chrome branco antigo, escolhida pelo usuário no
// menu da conta (`LogoutMenu`).
const onDarkText = colors.dark.text.primary;
const onDarkTextMuted = colors.dark.text.secondary;

const STORAGE_KEY = "appShellSidebarCollapsed";
export const SIDEBAR_WIDTH_EXPANDED = 260;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

// Preferência explícita do usuário (clique no toggle) é persistida e vence
// qualquer resize subsequente. Sem preferência salva, o colapso segue
// reativamente o breakpoint "lg" do tema (Seção 11.1: "Tablet: sidebar
// colapsada por padrão"), via useMediaQuery — não um valor fixo capturado
// só no mount.
const getStoredPreference = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? null : stored === "true";
  } catch {
    return null;
  }
};

const Sidebar = ({ chromeStyle = "blue" }) => {
  const { permissoes } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isBelowDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const [manualPreference, setManualPreference] = React.useState(getStoredPreference);
  const collapsed = manualPreference !== null ? manualPreference : isBelowDesktop;
  const isBlue = chromeStyle === "blue";

  const toggleCollapsed = () => {
    const next = !collapsed;
    setManualPreference(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // localStorage indisponível (ex.: modo privado) — preferência dura só a sessão em memória
    }
  };

  const itemsPermitidos = menuItems.filter((item) =>
    permissoes.some((permissao) => permissao.name === item.path),
  );

  const tier1 = itemsPermitidos.filter((item) => item.tier === 1);
  const estoqueItems = itemsPermitidos.filter((item) => item.tier === 2);
  const financeiroItems = itemsPermitidos.filter(
    (item) => item.tier === 3 && item.group === "Financeiro",
  );
  const utilitariosItems = itemsPermitidos.filter(
    (item) => item.tier === 3 && item.group === "Utilitários",
  );

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = location.pathname === `/${item.path}`;

    const button = (
      <ListItemButton
        selected={active}
        onClick={() => navigate(`/${item.path}`)}
        sx={{
          minHeight: 44,
          justifyContent: collapsed ? "center" : "flex-start",
          px: 2.5,
          ...(isBlue && {
            "&:hover": { bgcolor: "rgba(244, 241, 234, 0.08)" },
            "&.Mui-selected": { bgcolor: "rgba(194, 112, 61, 0.16)" },
            "&.Mui-selected:hover": { bgcolor: "rgba(194, 112, 61, 0.24)" },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 2,
            justifyContent: "center",
            color: active ? (isBlue ? "secondary.main" : "primary.main") : "inherit",
          }}
        >
          <Icon />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            slotProps={{
              primary: {
                fontWeight: active ? 600 : 400,
                noWrap: true,
                sx: {
                  fontSize: "0.8125rem",
                  color: active ? (isBlue ? "secondary.main" : "primary.main") : "inherit",
                },
              },
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <Tooltip
        key={item.path}
        title={collapsed ? item.fullLabel || item.label : ""}
        placement="right"
      >
        {button}
      </Tooltip>
    );
  };

  const renderGroup = (label, items) => {
    if (!items.length) return null;
    return (
      <React.Fragment key={label}>
        {!collapsed && (
          <ListSubheader
            component="div"
            sx={{
              bgcolor: "transparent",
              lineHeight: "32px",
              color: isBlue ? onDarkTextMuted : "text.secondary",
            }}
          >
            {label}
          </ListSubheader>
        )}
        {items.map(renderItem)}
      </React.Fragment>
    );
  };

  const dividerSx = isBlue ? { borderColor: "rgba(244, 241, 234, 0.12)" } : {};

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
          boxSizing: "border-box",
          bgcolor: isBlue ? "primary.dark" : "background.paper",
          color: isBlue ? onDarkText : "text.primary",
          ...(!isBlue && { borderRight: "1px solid", borderColor: "divider" }),
          overflowX: "hidden",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 1.5,
          minHeight: 64,
        }}
      >
        <IconButton
          onClick={toggleCollapsed}
          size="small"
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          sx={{ color: "inherit" }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
      <Divider sx={dividerSx} />
      <List component="nav" sx={{ py: 1 }}>
        {tier1.map(renderItem)}
        {estoqueItems.length > 0 && <Divider sx={{ my: 1, ...dividerSx }} />}
        {renderGroup("Estoque", estoqueItems)}
        {(financeiroItems.length > 0 || utilitariosItems.length > 0) && (
          <Divider sx={{ my: 1, ...dividerSx }} />
        )}
        {renderGroup("Financeiro", financeiroItems)}
        {renderGroup("Utilitários", utilitariosItems)}
      </List>
    </Drawer>
  );
};

export default Sidebar;
